import type { Sentence } from "@huosu/shared";

// Narrow, reviewed display corrections. Original imported records are retained.
const key = (text: string) => text.toLowerCase().replace(/[‘’]/g, "'").replace(/[.,!?;:]/g, "").replace(/\s+/g, " ").trim();
const corrections: Record<string, Partial<Sentence>> = {
  "we help visitors find the science room after discussing a traditional craft": {
    structure: [
      { label: "主语", text: "We", explain: "动作的执行者：我们" },
      { label: "谓语", text: "help", explain: "帮助" },
      { label: "宾语", text: "visitors", explain: "被帮助的人：访客" },
      { label: "宾补", text: "find the science room", explain: "省略 to 的不定式，补充说明帮助访客做什么" },
      { label: "时间状语", text: "after discussing a traditional craft", explain: "说明帮助行为发生在讨论传统工艺之后" }
    ]
  },
  "a small habit can make speaking easier": {
    structure: [
      { label: "主语", text: "A small habit", explain: "一个小习惯" },
      { label: "谓语", text: "can make", explain: "情态动词 can 与使役动词 make 构成谓语" },
      { label: "宾语", text: "speaking", explain: "动名词作宾语" },
      { label: "宾补", text: "easier", explain: "形容词比较级说明 speaking 的状态" }
    ]
  },
  "our class learned about a cultural symbol in the history hall yesterday": {
    structure: [
      { label: "主语", text: "Our class", explain: "我们班" },
      { label: "谓语", text: "learned about", explain: "动词与介词构成了解……的表达" },
      { label: "宾语", text: "a cultural symbol", explain: "了解的对象" },
      { label: "地点状语", text: "in the history hall", explain: "说明地点" },
      { label: "时间状语", text: "yesterday", explain: "说明时间" }
    ]
  },
  "i think we should start the meeting with a quick update": {
    chinese: "我认为会议开始时，我们应该先简要介绍一下最新进展。",
    phonetic: "/aɪ θɪŋk wiː ʃʊd stɑːrt ðə ˈmiːtɪŋ wɪð ə kwɪk ˈʌpdeɪt/",
    grammarTags: ["I think + 宾语从句（省略 that）", "should + 动词原形：应该……", "start … with …：以……开始"],
    structure: [
      { label: "主语", text: "I", explain: "我" },
      { label: "谓语", text: "think", explain: "认为，后接省略 that 的宾语从句" },
      { label: "从句主语", text: "we", explain: "我们" },
      { label: "从句谓语", text: "should start", explain: "应该开始" },
      { label: "宾语", text: "the meeting", explain: "会议" },
      { label: "方式状语", text: "with a quick update", explain: "以简要介绍最新进展的方式" }
    ]
  },
  "we need to set a date for the next meeting": {
    chinese: "我们需要确定下次会议的日期。",
    phonetic: "/wiː niːd tə set ə deɪt fə(r) ðə nekst ˈmiːtɪŋ/",
    grammarTags: ["need to do", "set a date", "for + 名词短语"],
    structure: [
      { label: "主语", text: "We", explain: "我们" },
      { label: "谓语", text: "need", explain: "需要" },
      { label: "不定式宾语", text: "to set a date", explain: "确定一个日期" },
      { label: "目的状语", text: "for the next meeting", explain: "用于下一次会议" }
    ]
  },
  "every time i see you i cannot help but be reminded of your mother": {
    chinese: "每次见到你，我都会不由得想起你的母亲。", grammarTags: ["一般现在时", "时间状语从句", "cannot help but + 动词原形"],
    structure: [
      { label: "时间状语从句", text: "Every time I see you", explain: "每次见到你" },
      { label: "主语", text: "I", explain: "我" },
      { label: "谓语", text: "cannot help but be reminded", explain: "不由得想起；be reminded 为被动形式" },
      { label: "介词短语", text: "of your mother", explain: "想起的对象：你的母亲" }
    ]
  },
  "our company is a limited company": {
    chinese: "我们公司是一家有限公司。", grammarTags: ["一般现在时", "主系表"],
    structure: [{ label: "主语", text: "Our company", explain: "我们公司" }, { label: "系动词", text: "is", explain: "是" }, { label: "表语", text: "a limited company", explain: "一家有限公司" }]
  },
  "i need to travel": {
    chinese: "我需要去旅行。", grammarTags: ["一般现在时", "need to do"],
    structure: [{ label: "主语", text: "I", explain: "我" }, { label: "谓语动词", text: "need", explain: "需要" }, { label: "不定式补足成分", text: "to travel", explain: "去旅行" }]
  },
  "he should have done it that way": { chinese: "他本该那样做的。", grammarTags: ["should have done：对过去的评价"], structure: [] },
  "one must learn a lesson from the difficult situations and bad conditions in one's life": { chinese: "人必须从生活中的困境和不利处境中吸取教训。", grammarTags: [], structure: [] },
  "her smooth entry into the ranks of the royal family will help to dispel these fears": { chinese: "她顺利融入王室将有助于消除这些担忧。", grammarTags: ["一般将来时"], structure: [] }
};

const primaryCoreMeanings: Record<string, string> = {
  cat: "猫", art: "美术；艺术", pear: "梨", apple: "苹果", orange: "橙子；橙色", dog: "狗", bird: "鸟", fish: "鱼",
  school: "学校", book: "书", pen: "钢笔；笔", pencil: "铅笔", ruler: "尺子", rubber: "橡皮", family: "家；家人",
  mother: "母亲；妈妈", father: "父亲；爸爸", sister: "姐妹", brother: "兄弟", table: "桌子", chair: "椅子", desk: "书桌；课桌",
  water: "水", milk: "牛奶", day: "一天；日子；白天"
};

export type StudyWordDetail = { phonetic: string; partOfSpeech: string; zh: string };

// Reviewed fallback for high-frequency words. The managed word bank remains
// the first data source and takes precedence whenever it has a record.
const studyWordFallbacks: Record<string, StudyWordDetail> = {
  i: { phonetic: "/aɪ/", partOfSpeech: "pron.", zh: "我" },
  think: { phonetic: "/θɪŋk/", partOfSpeech: "v.", zh: "认为；想" },
  should: { phonetic: "/ʃʊd/", partOfSpeech: "modal.", zh: "应该" },
  start: { phonetic: "/stɑːrt/", partOfSpeech: "v.", zh: "开始" },
  with: { phonetic: "/wɪð/", partOfSpeech: "prep.", zh: "以；用；和" },
  quick: { phonetic: "/kwɪk/", partOfSpeech: "adj.", zh: "快速的；简短的" },
  update: { phonetic: "/ˈʌpdeɪt/", partOfSpeech: "n.", zh: "最新情况；进展" },
  we: { phonetic: "/wiː/", partOfSpeech: "pron.", zh: "我们" },
  need: { phonetic: "/niːd/", partOfSpeech: "v.", zh: "需要" },
  to: { phonetic: "/tə/", partOfSpeech: "inf.", zh: "（用于动词原形前）" },
  set: { phonetic: "/set/", partOfSpeech: "v.", zh: "确定；安排" },
  a: { phonetic: "/ə/", partOfSpeech: "art.", zh: "一（个）" },
  date: { phonetic: "/deɪt/", partOfSpeech: "n.", zh: "日期" },
  for: { phonetic: "/fər/", partOfSpeech: "prep.", zh: "为了；用于" },
  the: { phonetic: "/ðə/", partOfSpeech: "art.", zh: "这；那（定冠词）" },
  next: { phonetic: "/nekst/", partOfSpeech: "adj.", zh: "下一个的" },
  meeting: { phonetic: "/ˈmiːtɪŋ/", partOfSpeech: "n.", zh: "会议" }
};

export function studyWordDetail(word: string): StudyWordDetail | undefined {
  return studyWordFallbacks[key(word)];
}

export function learningPresentation(sentence: Sentence, primaryWordbook: boolean): Sentence {
  const correction = corrections[key(sentence.english)];
  if (correction) return { ...sentence, ...correction };
  const core = primaryWordbook ? primaryCoreMeanings[key(sentence.english)] : undefined;
  return core ? { ...sentence, chinese: `常用义：${core}` } : sentence;
}
